import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import i18next from "i18next";
import { Button, Flex } from "antd";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import Cookies from "universal-cookie";
import { ExportOutlined } from "@ant-design/icons";

import { downloadBlob } from "components/Utils/actions";
import { useDownloadFinDataForDistributorInExcel, useGetDistributorFinData, } from "api/reactQuery/queryComponents";
import { BSCoreServicesDtoMarketplaceFinDataDetailDto } from "api/reactQuery/querySchemas";
import { COLUMN_TYPE_NAMES, getGridDefaultProps, } from "constants/agGridConstants";

import FinancialInfoCards from "./components/FinancialInfoCards";
import BonusOnInvoiceTable from "./components/BonusOnInvoiceTable";
import { currencyFormatter } from "components/Utils/agGrid/valueFormatters";
import NcpCompensationTable from "./components/NcpCompensationTable";

const FinancialInfo = () => {
  const [id, setId] = useState<number | null>(null);

  const colDefs = useMemo<ColDef<BSCoreServicesDtoMarketplaceFinDataDetailDto>[]>(() =>
    [
      { field: "productType", headerName: i18next.t("fi_table_product_type") },
      {
        field: "documentType",
        headerName: i18next.t("fi_table_document_type"),
      },
      { field: "documentNo", headerName: i18next.t("fi_table_document_no") },
      {
        field: "documentDate",
        headerName: i18next.t("fi_table_document_date"),
        type: COLUMN_TYPE_NAMES.DATE_COLUMN,
      },
      {
        field: "dueDate",
        headerName: i18next.t("fi_table_due_date"),
        type: COLUMN_TYPE_NAMES.DATE_COLUMN,
      },
      {
        field: "due",
        headerName: i18next.t("fi_table_due"),
        valueFormatter: currencyFormatter(),
      },
      {
        field: "inTerm",
        headerName: i18next.t("fi_table_in_term"),
        valueFormatter: currencyFormatter(),
      },
      {
        field: "balance",
        headerName: i18next.t("fi_table_balance"),
        valueFormatter: currencyFormatter(),
      },
    ],
    [],
  );

  useEffect(() => {
    const cookies = new Cookies();
    const token = cookies.get("token");
    const distributorId = token?.["DistributorId"] ?? null;
    setId(distributorId);
  }, []);

  const { data, isLoading } = useGetDistributorFinData({
    queryParams: { distributorId: id },
  });

  const {
    mutate: downloadFinDataForDistributorInExcel,
    isLoading: downloadFinDataForDistributorInExcelLoading,
  } = useDownloadFinDataForDistributorInExcel({
    onSuccess: (data) =>
      downloadBlob(
        (data as unknown as { blob: Blob }).blob,
        `FinancialInfo${id}.xlsx`,
      ),
  });

  if (isLoading || !id) return null;

  return (
    <>
      <Flex justify="end">
        <Button
          style={{ padding: "0 50px" }}
          type="primary"
          loading={downloadFinDataForDistributorInExcelLoading}
          onClick={() => downloadFinDataForDistributorInExcel({ body: id })}
          disabled={!id}
        >
          <ExportOutlined style={{ verticalAlign: "middle" }} />
          {i18next.t("export_in_excel")}
        </Button>
      </Flex>

      {!!data?.bonusOnInvoiceDetails && (
        <BonusOnInvoiceTable bonusOnInvoiceData={data.bonusOnInvoiceDetails} />
      )}

      {!!data?.ncpCompensationDetails && (
        <NcpCompensationTable ncpData={data.ncpCompensationDetails} />
      )}

      {data && (
        <h1 style={{ fontSize: 30, marginBottom: 12 }}>
          {i18next.t("financial_information")}
        </h1>
      )}

      <FinancialInfoCards distributorFinancialData={data} isLoadingFinancialData={isLoading} />

      <AgGridReact
        columnDefs={colDefs}
        rowData={data.details}
        {...getGridDefaultProps()}
      />

      {data?.dateOfImport && (
        <div
          style={{
            color: "#6d6d6d",
            fontSize: 12,
            marginTop: 8,
            textAlign: "left",
          }}
        >
          {`${i18next.t("fi_date_of_import")}: ${dayjs(
            data.dateOfImport,
          ).format("DD.MM.YYYY HH:mm")}`}
        </div>
      )}
    </>
  );
};

export default FinancialInfo;
